import querystring from 'querystring'

import PropTypes from 'prop-types'
import React, { useState, useCallback } from 'react'
import config from 'config'
import { useForm } from 'react-hook-form'

import { AppVerticalProvider } from '@cred/shared/AppVerticalContext'
import { required } from '@cred/shared/validations'

import { minMaxLoanAmount, minMaxLoanAmountWithBhg } from 'personal-loan/rate-table/validations'
import LendersList from 'components/lenders-list'
import Pagination from 'components/styled-pagination'
import PersonalFilterForm from 'components/filter-form'
import withRateScript from 'components/hocs/with-rate-script'
import { PERSONAL_FILTER_FORM_INITIAL_VALUES as initialValues } from 'common/constants'
import useActiveLenders from 'components/hooks/useActiveLenders'
import { filterLender, loadLendersRates } from 'common/utils/rates'

import {
  generateFilterText,
  getCreditScore,
  getLoanAmount,
  getPageStats,
  getPartnerDefaultFilterValues,
} from '../utils'
import FilterModal from '../components/filter-modal'
import { getFilteredLenders } from '../../../utils/lenders'

const paginationName = 'personal-rate-table'
const LENDER_PER_PAGE = 3

const getCtaUrl = (utms, omitUtmParams, metaQuery) => {
  const params = {
    utm_source: utms.utm_source,
    utm_medium: utms.utm_medium,
    utm_campaign: utms.utm_campaign,
    utm_content: utms.utm_content,
    credclid: utms.credclid,
    renderId: utms.renderId,
  }

  if (omitUtmParams) {
    return `${config.credibleRoot}/personal-loans/borrower${metaQuery}`
  }

  return `${config.credibleRoot}/personal-loans/borrower?${querystring.encode(params)}${metaQuery}`
}

/* Get lenders rates from Rates library. */
const getLenders = (props, formFilterValues, availableLenders) => {
  const { isScriptLoaded } = props
  const creditScore = getCreditScore(formFilterValues.creditScoreApprox)
  const loanAmount = getLoanAmount(formFilterValues.loanAmount)

  /* Get the rates after loading the ratesLibrary JS and do it only once. */
  if (isScriptLoaded) {
    return loadLendersRates({
      availableLenders: filterLender(availableLenders, creditScore, loanAmount),
      filters: { vertical: 'personal' },
    })
  }

  return []
}

const PersonalLoanRateTable = ({
  utms,
  variation,
  initialize,
  widgetContext,
  omitUtmParams = false,
  metaQuery,
  initialValues: widgetInitialValues,
  theme,
  ...props
}) => {
  const defaultValues = {
    ...initialValues,
    ...getPartnerDefaultFilterValues(widgetContext, 'personal-loan'),
  }
  const { control, formState, reset, getValues } = useForm({
    defaultValues,
    mode: 'onChange',
  })
  const [page, setPage] = useState(0)

  const ctaUrl = getCtaUrl(utms, omitUtmParams, metaQuery)
  const [filterValues, setFilterValues] = useState(defaultValues)
  const fetchedLenders = useActiveLenders('personal')

  const availableLenders = getFilteredLenders({
    lenders: fetchedLenders,
    parentPageUrl: widgetInitialValues?.options?.partnerPageUrl,
  })

  const minMaxLoanAmountValidation = useCallback(
    (value) => {
      const isBhgAvailable = availableLenders.some((lender) => lender.slug === 'bhg')
      return isBhgAvailable ? minMaxLoanAmountWithBhg(value) : minMaxLoanAmount(value)
    },
    [availableLenders],
  )

  const lenders = getLenders(props, filterValues, availableLenders)

  const { pageCount, pageFrom, pageTo, showPagination } = getPageStats(
    variation,
    lenders,
    page,
    LENDER_PER_PAGE,
  )

  const cancel = (closeModal) => {
    closeModal()
    // Setting timeout so the form doesn't reset as the modal is disappearing
    setTimeout(() => {
      reset(filterValues)
    }, 500)
  }

  const onUpdate = (closeModal) => {
    const { creditScoreApprox, loanAmount } = getValues()
    setFilterValues({ creditScoreApprox, loanAmount })
    closeModal()
  }

  const onPageChange = (_, selectedPage) => {
    setPage(selectedPage)
  }

  const onReset = () => {
    reset(defaultValues)
    setFilterValues(defaultValues)
  }

  return (
    <AppVerticalProvider value="pl">
      <div>
        <FilterModal formName="personal-filter-form" filterText={generateFilterText(filterValues)}>
          {({ closeModal }) => (
            <PersonalFilterForm
              control={control}
              withFooter
              update={() => onUpdate(closeModal)}
              cancel={() => cancel(closeModal)}
              customValidation={[required, minMaxLoanAmountValidation]}
              invalid={formState.isDirty && !formState.isValid}
            />
          )}
        </FilterModal>
        <LendersList
          theme={theme}
          lenders={lenders.slice(pageFrom, pageTo)}
          ctaUrl={ctaUrl}
          name="multiVerticalPersonal"
          resetFilters={onReset}
        />
        {showPagination && (
          <Pagination onChange={onPageChange} pageCount={pageCount} name={paginationName} />
        )}
      </div>
    </AppVerticalProvider>
  )
}

PersonalLoanRateTable.propTypes = {
  page: PropTypes.number,
  variation: PropTypes.string,
  initialize: PropTypes.func,
  utms: PropTypes.shape({
    utm_source: PropTypes.string,
    utm_campaign: PropTypes.string,
    utm_medium: PropTypes.string,
    utm_content: PropTypes.string,
    credclid: PropTypes.string,
    renderId: PropTypes.string,
  }),
  omitUtmParams: PropTypes.bool,
  widgetContext: PropTypes.object,
  metaQuery: PropTypes.string,
  initialValues: PropTypes.shape({
    options: PropTypes.shape({
      partnerPageUrl: PropTypes.string,
    }),
  }),
  theme: PropTypes.string.isRequired,
}

export { PersonalLoanRateTable as PersonalLoanRateTableNoHoc, getCtaUrl, getLenders }

export default withRateScript(PersonalLoanRateTable)
